import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { appServicesAPI, clustersAPI, catalogAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ImageSelector from '../../components/ImageSelector';
import useProvisionSettle from '../../hooks/useProvisionSettle';

const STATUS_VARIANT = {
  running:          '',
  pending:          'paused',
  failed:           'error',
  CrashLoopBackOff: 'error',
  OOMKilled:        'error',
  ImagePullBackOff: 'error',
  Error:            'error',
  unknown:          'draft',
};
const CRASH_REASONS = ['CrashLoopBackOff', 'OOMKilled', 'Error', 'ImagePullBackOff', 'ErrImagePull'];

function StatusBadge({ status, reason, restarts }) {
  const key = reason && CRASH_REASONS.includes(reason) ? reason : (status || 'unknown').toLowerCase();
  const variant = key in STATUS_VARIANT ? STATUS_VARIANT[key] : STATUS_VARIANT.unknown;
  const label = reason || status || 'unknown';
  return (
    <span className={`status${variant ? ' ' + variant : ''}`}>
      <span className="pulse" />
      {label}
      {restarts > 0 && <span style={{ opacity: 0.7, fontSize: 10 }}>({restarts}↺)</span>}
    </span>
  );
}

function KindBadge({ kind }) {
  const variants = { Deployment: 'brand', StatefulSet: 'accent', raw: '' };
  const variant = kind in variants ? variants[kind] : variants.raw;
  return (
    <span className={`badge${variant ? ' ' + variant : ''}`}>
      {kind}
    </span>
  );
}

export default function WorkloadDetailPage() {
  const { hasSectionAction } = useAuth();
  const canEdit = hasSectionAction('workloads', 'edit');
  const canDelete = hasSectionAction('workloads', 'delete');
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [workload, setWorkload]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  // ?tab=yaml deep-links straight to the YAML editor — used by My Workloads' "Edit" menu
  // item, so picking Edit there doesn't land on Overview first and need a second click.
  const [tab, setTab]                 = useState(searchParams.get('tab') || 'overview');
  const [liveStatus, setLiveStatus]   = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [clusters, setClusters]       = useState([]);
  const [conn, setConn]               = useState(null); // catalog connection info (host/port/user/password)
  const [connLoading, setConnLoading] = useState(false);

  // Scale state
  const [scaleInput, setScaleInput]   = useState('');
  const [scaling, setScaling]         = useState(false);
  const [scaleMsg, setScaleMsg]       = useState('');

  // Update-image state (rolling update to a new image version)
  const [newImage, setNewImage]       = useState('');
  const [updatingImg, setUpdatingImg] = useState(false);
  const [imgMsg, setImgMsg]           = useState('');

  // Logs state
  const [logs, setLogs]               = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError]     = useState('');
  const [tail, setTail]               = useState(100);
  const [previous, setPrevious]       = useState(false);
  const logsRef = useRef();

  // Terminal state
  const [termInput, setTermInput]     = useState('');
  const [termHistory, setTermHistory] = useState([]);
  const [termRunning, setTermRunning] = useState(false);
  const termBottomRef = useRef();

  // YAML history state — the data (AppServiceManifest rows) and API endpoint
  // (GET /app-services/{id}/manifests) already existed before this; nothing in My
  // Workloads' UI called it, so there was no way to see or edit what was actually
  // applied short of going to a separate, unrelated "Kubernetes Resources" browser.
  const [manifests, setManifests]         = useState([]);
  const [manifestsLoading, setManifestsLoading] = useState(false);
  const [selectedManifestId, setSelectedManifestId] = useState(null);
  const [yamlDraft, setYamlDraft]         = useState('');
  const [yamlDirty, setYamlDirty]         = useState(false);
  const [applyingYaml, setApplyingYaml]   = useState(false);
  const [yamlMsg, setYamlMsg]             = useState('');

  const loadWorkload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Fast first paint: DB-only (live=false) avoids the ~4s kubectl enrich blocking render.
      const [svcRes, clRes] = await Promise.all([
        appServicesAPI.get(id, false),
        clustersAPI.list(),
      ]);
      setWorkload(svcRes.data);
      setScaleInput(String(svcRes.data.replicas || 1));
      setClusters(clRes.data || []);
      // Background enrich: fetch live image/resources without blocking the page.
      appServicesAPI.get(id, true)
        .then((r) => setWorkload((prev) => ({ ...prev, ...r.data })))
        .catch(() => {});
      // Catalog-managed workloads (notes starts "Catalog:") expose live connection info —
      // fetch it so the password/host show here (no need to keep creds in Excel).
      if (String(svcRes.data.notes || '').startsWith('Catalog:')) {
        setConnLoading(true);
        catalogAPI.connection(svcRes.data.name, svcRes.data.cluster_id)
          .then((r) => setConn(r.data)).catch(() => setConn(null))
          .finally(() => setConnLoading(false));
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load workload');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await appServicesAPI.liveStatus(id);
      setLiveStatus(r.data);
    } catch (e) {}
    finally { setStatusLoading(false); }
  }, [id]);

  useEffect(() => { loadWorkload(); }, [loadWorkload]);

  // A YAML deploy lands here before its apply has run — watch until it settles so a
  // rejected manifest shows the "Deploy failed" banner below instead of "provisioning".
  useProvisionSettle(id, workload?.status, (row) => {
    setWorkload(prev => ({ ...prev, status: row.status, notes: row.notes, name: row.name }));
  });
  useEffect(() => { if (tab === 'overview') loadStatus(); }, [tab, loadStatus]);

  // Logs
  const fetchLogs = useCallback(async (t = tail, prev = previous) => {
    setLogsLoading(true); setLogsError(''); setLogs('');
    try {
      const r = await appServicesAPI.getLogs(id, null, t, prev);
      setLogs(r.data.logs || '');
      if (r.data.error) setLogsError(r.data.error);
    } catch (e) {
      setLogsError(e.response?.data?.detail || 'Failed to fetch logs');
    } finally { setLogsLoading(false); }
  }, [id, tail, previous]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs(tail, previous);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // YAML history
  const fetchManifests = useCallback(async () => {
    setManifestsLoading(true);
    try {
      const r = await appServicesAPI.getManifestHistory(id);
      const rows = r.data?.manifests || [];
      setManifests(rows);
      if (rows.length) {
        setSelectedManifestId(rows[0].id);
        setYamlDraft(rows[0].yaml || '');
        setYamlDirty(false);
      }
    } catch (e) {
      setYamlMsg(e.response?.data?.detail || 'Failed to load YAML history');
    } finally {
      setManifestsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (tab === 'yaml') fetchManifests();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectManifest = (m) => {
    setSelectedManifestId(m.id);
    setYamlDraft(m.yaml || '');
    setYamlDirty(false);
    setYamlMsg('');
  };

  // Re-applies via the SAME raw_yaml path a YAML-mode create already uses — the backend's
  // create route exempts any call carrying raw_yaml from its name-collision guard
  // (app_services.py: `_allow_update = bool(body.get("allow_update")) or bool(raw_yaml)`),
  // so this updates the existing row in place rather than creating a second one.
  const handleApplyYaml = async () => {
    if (!yamlDraft.trim()) { setYamlMsg('YAML content is empty'); return; }
    if (!window.confirm('Re-apply this YAML to the live cluster? This updates the running workload.')) return;
    setApplyingYaml(true); setYamlMsg('');
    try {
      await appServicesAPI.create({
        cluster_id: workload.cluster_id,
        name: workload.name,
        display_name: workload.display_name || workload.name,
        image: workload.image || 'n/a',
        kind: workload.kind,
        namespace: workload.namespace,
        raw_yaml: yamlDraft,
        allow_update: true,
      });
      setYamlMsg('Applied — refreshing…');
      setYamlDirty(false);
      setTimeout(() => { fetchManifests(); loadWorkload(); loadStatus(); }, 1500);
    } catch (e) {
      setYamlMsg(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyingYaml(false);
    }
  };

  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs]);

  // Terminal
  const handleTermExec = async () => {
    if (!termInput.trim()) return;
    const cmd = termInput.trim();
    setTermInput('');
    setTermHistory(h => [...h, { type: 'input', text: `$ ${cmd}` }]);
    setTermRunning(true);
    try {
      const r = await appServicesAPI.execInService(id, cmd);
      if (r.data.stdout) setTermHistory(h => [...h, { type: 'output', text: r.data.stdout }]);
      if (r.data.stderr) setTermHistory(h => [...h, { type: 'error',  text: r.data.stderr }]);
      if (!r.data.stdout && !r.data.stderr) setTermHistory(h => [...h, { type: 'output', text: '(no output)' }]);
    } catch (e) {
      setTermHistory(h => [...h, { type: 'error', text: e.response?.data?.detail || 'Command failed' }]);
    } finally { setTermRunning(false); }
  };

  useEffect(() => {
    if (termBottomRef.current) termBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [termHistory]);

  const handleRestart = async () => {
    try {
      await appServicesAPI.restart(id);
      setTimeout(loadStatus, 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Restart failed'); }
  };

  const handleScale = async () => {
    const reps = parseInt(scaleInput);
    if (isNaN(reps) || reps < 0 || reps > 50) { setScaleMsg('Enter a valid replica count (0–50)'); return; }
    setScaling(true); setScaleMsg('');
    try {
      await appServicesAPI.scale(id, reps);
      setScaleMsg(`Scaled to ${reps} replica${reps !== 1 ? 's' : ''}`);
      loadWorkload();
      setTimeout(() => setScaleMsg(''), 3000);
    } catch (e) {
      setScaleMsg(e.response?.data?.detail || 'Scale failed');
    } finally { setScaling(false); }
  };

  const handleUpdateImage = async () => {
    const img = (newImage || '').trim();
    if (!img) { setImgMsg('Pick or enter a new image'); return; }
    setUpdatingImg(true); setImgMsg('');
    try {
      const r = await appServicesAPI.updateImage(id, img);
      setImgMsg(`Rolling update → ${r.data?.image || img}`);
      loadWorkload(); setTimeout(loadStatus, 2500);
      setTimeout(() => setImgMsg(''), 5000);
    } catch (e) {
      setImgMsg(e.response?.data?.detail || 'Image update failed');
    } finally { setUpdatingImg(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete workload "${workload?.name}"? This removes the K8s deployment and all associated resources.`)) return;
    try {
      await appServicesAPI.delete(id);
      navigate('/compute/workloads');
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Force-restart ONE misbehaving replica, rather than the only options that existed
  // before this — restart ALL pods (rollout restart) or delete the whole workload. Its
  // owning controller (ReplicaSet/StatefulSet) recreates the pod immediately.
  const [deletingPod, setDeletingPod] = useState(null); // pod name currently being deleted
  const handleDeletePod = async (podName) => {
    if (!window.confirm(`Delete pod "${podName}"? Its controller will recreate it.`)) return;
    setDeletingPod(podName);
    try {
      await appServicesAPI.deletePod(id, podName);
      setTimeout(loadStatus, 2000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete pod');
    } finally {
      setDeletingPod(null);
    }
  };

  const handleDownloadLogs = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${workload?.name}-logs.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--muted-2)' }}>Loading workload...</div>;
  if (error)   return <div style={{ padding: 40, color: 'var(--color-danger-500)' }}>{error}</div>;
  if (!workload) return null;

  const clusterName = clusters.find(c => c.id === workload.cluster_id)?.name || `cluster-${workload.cluster_id}`;
  const pods = liveStatus?.pods || [];
  const overallStatus = liveStatus?.status || workload.status;
  const crashReason = pods.find(p => p.reason && CRASH_REASONS.includes(p.reason))?.reason;
  const totalRestarts = pods.reduce((s, p) => s + (p.restarts || 0), 0);

  // Resource display: live cluster values win; "None" when a side is unset; DB fallback otherwise.
  const lr = workload.live_resources;
  const reqLim = (req, lim) => {
    if (!req && !lim) return 'None';
    return `${req || '—'} req → ${lim || '—'} lim`;
  };
  const cpuDisplay = lr
    ? reqLim(lr.cpu_req, lr.cpu_lim)
    : `${workload.cpu_request} req → ${workload.cpu_limit} lim`;
  const ramDisplay = lr
    ? reqLim(lr.mem_req, lr.mem_lim)
    : `${workload.mem_request_gb}G req → ${workload.mem_limit_gb}G lim`;

  const TABS = [
    { id: 'overview', label: '⊞ Overview' },
    { id: 'logs',     label: '📋 Logs' },
    { id: 'terminal', label: '⬛ Terminal' },
    { id: 'yaml',     label: '📄 YAML' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: 'var(--muted)' }}>
        <span style={{ cursor: 'pointer', color: 'var(--brand)' }} onClick={() => navigate('/compute/workloads')}>
          My Workloads
        </span>
        <span>›</span>
        <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{workload.display_name || workload.name}</span>
      </div>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--brand-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
            {workload.kind === 'StatefulSet' ? '🗄️' : '🚀'}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>
                {workload.display_name || workload.name}
              </h1>
              <KindBadge kind={workload.kind} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--muted)' }}>
              <span>{clusterName}</span>
              <span>·</span>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{workload.namespace}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusBadge status={overallStatus} reason={crashReason} restarts={totalRestarts} />

          {/* Inline scale control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              className="input"
              type="number" min="0" max="50"
              value={scaleInput}
              onChange={e => setScaleInput(e.target.value)}
              style={{ width: 56, textAlign: 'center' }}
            />
            {canEdit && (
            <button onClick={handleScale} disabled={scaling} className="btn btn-tinted-success btn-sm">
              {scaling ? '...' : 'Scale'}
            </button>
            )}
          </div>
          {scaleMsg && (
            <span style={{ fontSize: 12, color: scaleMsg.includes('failed') || scaleMsg.includes('valid') ? 'var(--color-danger-500)' : 'var(--color-success-700)' }}>
              {scaleMsg}
            </span>
          )}

          {canEdit && (
          <button onClick={handleRestart} className="btn btn-secondary btn-sm">
            ↺ Restart
          </button>
          )}
          {canDelete && (
          <button onClick={handleDelete} className="btn btn-tinted-danger btn-sm">
            🗑 Delete
          </button>
          )}
        </div>

        {/* Update image — rolling update to a new version (build→version→deploy loop) */}
        {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12,
                      paddingTop: 12, borderTop: '1px dashed var(--surface-2)' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>Update image:</span>
          <div style={{ minWidth: 320, flex: '1 1 320px', maxWidth: 480 }}>
            <ImageSelector value={newImage} onChange={setNewImage}
              placeholder={`current: ${workload?.image || '—'} — pick a new version`} />
          </div>
          <button onClick={handleUpdateImage} disabled={updatingImg || !newImage}
            className="btn btn-primary btn-sm">
            {updatingImg ? 'Updating…' : '↑ Rolling update'}
          </button>
          {imgMsg && (
            <span style={{ fontSize: 12, color: imgMsg.toLowerCase().includes('fail') ? 'var(--color-danger-500)' : 'var(--color-success-700)' }}>
              {imgMsg}
            </span>
          )}
        </div>
        )}
      </div>

      {workload.status === 'failed' && workload.notes && !crashReason && (
        <div className="banner danger" style={{ marginBottom: 16 }}>
          <span>Deploy failed: {
            // `notes` for catalog releases is "Catalog: <app>/<variant> ... | FAILED: <reason>"
            // — the breadcrumb prefix isn't useful to a human, only the reason after it is.
            workload.notes.includes('| FAILED:')
              ? workload.notes.split('| FAILED:').pop().trim()
              : workload.notes
          }</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-underline" style={{ marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={tab === t.id ? 'active' : undefined}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Config card */}
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 16 }}>Configuration</div>
            {[
              ['Image',     workload.image],
              ['Kind',      workload.kind],
              ['Replicas',  String(workload.replicas ?? '—')],
              // Prefer LIVE cluster requests/limits over the stored defaults; show
              // "None" when the workload declares no requests/limits (rather than the
              // fabricated "1 req → 2 lim"). Fall back to DB values only when live is absent.
              ['CPU',       cpuDisplay],
              ['RAM',       ramDisplay],
              ['Storage',   workload.storage_gb ? `${workload.storage_gb}GB` : 'None'],
              ['Cluster',   clusterName],
              ['Namespace', workload.namespace],
              ['Created',   workload.created_at ? new Date(workload.created_at).toLocaleString() : '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--ink)',
                  fontFamily: label === 'Image' ? 'monospace' : 'inherit',
                  textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Connection card — catalog-managed DBs only. Password read live from the K8s
              secret; shown here so credentials live in one place (not spreadsheets). */}
          {(conn || connLoading) && (
            <div className="card">
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 16 }}>Connection</div>
              {connLoading ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading…</div> : conn && [
                ['Host',     conn.host],
                ['Port',     conn.port],
                ['Username', conn.username],
                ['Password', conn.password],
              /* Coerce to text. React throws #31 ("objects are not valid as a React child")
                 and blanks the whole page if any value arrives as an object — which happened
                 when the API returned a callable serialised as {}. A detail page should not be
                 destroyed by one unexpected field. */
              ].map(([label, raw]) => {
                const value = raw == null ? '—'
                  : typeof raw === 'object' ? JSON.stringify(raw)
                  : String(raw);
                return (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink)', fontFamily: 'monospace',
                    textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{value}</span>
                </div>
                );
              })}
            </div>
          )}

          {/* Live status card */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Live Status
              </div>
              <button onClick={loadStatus} disabled={statusLoading} className="btn btn-secondary btn-sm">
                {statusLoading ? '...' : '⟳ Refresh'}
              </button>
            </div>

            {!liveStatus && !statusLoading && (
              <div style={{ color: 'var(--muted-2)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                Click Refresh to load live status
              </div>
            )}

            {pods.length > 0 && (
              <div>
                {/* Pods table */}
                <div className="table-card">
                  <table className="ds">
                    <thead>
                      <tr>
                        {['Pod', 'Phase', 'Ready', 'Restarts', 'Actions'].map(h => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pods.map((pod, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pod.name}>{pod.name}</td>
                          <td>
                            <StatusBadge status={pod.phase} reason={pod.reason} restarts={pod.restarts} />
                          </td>
                          <td style={{ color: pod.ready ? 'var(--color-success-700)' : 'var(--color-danger-500)', fontWeight: 600 }}>
                            {pod.ready ? 'Yes' : 'No'}
                          </td>
                          <td style={{ color: (pod.restarts||0) > 0 ? 'var(--color-warning-700)' : 'var(--muted-2)' }}>
                            {pod.restarts || 0}
                          </td>
                          <td>
                            {canEdit && workload.kind !== 'Pod' && (
                              <button onClick={() => handleDeletePod(pod.name)}
                                disabled={deletingPod === pod.name}
                                title="Delete this pod — its controller recreates it fresh"
                                className="btn btn-tinted-danger btn-sm">
                                {deletingPod === pod.name ? '…' : '🗑 Delete pod'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Crash banner */}
                {crashReason && (
                  <div className="banner danger" style={{ marginTop: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Container is crashing ({crashReason}). Check logs for details.</span>
                    <button onClick={() => { setTab('logs'); setPrevious(true); }}
                      className="btn btn-danger btn-sm">
                      View Logs →
                    </button>
                  </div>
                )}
              </div>
            )}

            {liveStatus?.events?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
                  letterSpacing: '0.05em', marginBottom: 8 }}>Recent Events</div>
                {liveStatus.events.slice(-5).map((ev, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--ink-2)', padding: '4px 0',
                    borderBottom: '1px solid var(--surface-2)', fontFamily: 'monospace' }}>
                    {ev}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LOGS TAB ── */}
      {tab === 'logs' && (
        <div style={{ background: '#0d1117', borderRadius: 10, overflow: 'hidden', height: 'calc(100vh - 280px)' }}>
          <div style={{ background: '#161b22', padding: '10px 16px', borderBottom: '1px solid #30363d',
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <select value={tail} onChange={e => { setTail(Number(e.target.value)); fetchLogs(Number(e.target.value), previous); }}
              style={{ background: '#21262d', color: '#e6edf3', border: '1px solid #30363d',
                borderRadius: 5, padding: '4px 8px', fontSize: 12 }}>
              {[50, 100, 200, 500, 1000].map(n => <option key={n} value={n}>Last {n}</option>)}
            </select>
            <button onClick={() => { const next = !previous; setPrevious(next); fetchLogs(tail, next); }}
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 5, cursor: 'pointer', fontWeight: 600,
                background: previous ? 'var(--ink-2)' : '#21262d',
                color: previous ? 'var(--color-warning-500)' : '#e6edf3',
                border: previous ? '1px solid var(--color-warning-500)' : '1px solid #30363d' }}>
              ⚡ Previous container
            </button>
            <button onClick={() => fetchLogs(tail, previous)}
              style={{ padding: '4px 12px', fontSize: 12, background: '#21262d', color: '#e6edf3',
                border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer' }}>
              ↺ Reload
            </button>
            <button onClick={handleDownloadLogs} disabled={!logs || logsLoading}
              style={{ padding: '4px 12px', fontSize: 12, background: '#21262d', color: '#e6edf3',
                border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer',
                opacity: (!logs || logsLoading) ? 0.4 : 1 }}>
              ⬇ Download
            </button>
            <div style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'monospace',
              color: logsLoading ? 'var(--brand)' : logsError ? 'var(--color-danger-500)' : 'var(--color-success-500)' }}>
              {logsLoading ? '⟳ Loading...' : logsError ? `✗ ${logsError}` : logs ? `✓ ${logs.split('\n').length} lines` : ''}
            </div>
          </div>
          <div ref={logsRef} style={{ flex: 1, overflowY: 'auto', height: 'calc(100% - 45px)',
            padding: '8px 0', fontFamily: "'Courier New', monospace", fontSize: 12.5, lineHeight: 1.6 }}>
            {logsLoading && (
              <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px' }}>Fetching logs...</div>
            )}
            {!logsLoading && logsError && !logs && (
              <div style={{ color: 'var(--color-danger-500)', padding: '20px', fontFamily: 'monospace', fontSize: 12 }}>
                {logsError}
                {!previous && (
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setPrevious(true); fetchLogs(tail, true); }}
                      style={{ padding: '5px 14px', background: 'var(--color-warning-500)', color: 'var(--ink)',
                        border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      ⚡ Try previous container logs
                    </button>
                  </div>
                )}
              </div>
            )}
            {!logsLoading && !logs && !logsError && (
              <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px', fontSize: 13 }}>
                No logs available.
                {!previous && (
                  <div style={{ marginTop: 10, fontSize: 12 }}>
                    Pod crashing?{' '}
                    <span onClick={() => { setPrevious(true); fetchLogs(tail, true); }}
                      style={{ color: 'var(--color-warning-500)', cursor: 'pointer', textDecoration: 'underline' }}>
                      View previous container logs
                    </span>
                  </div>
                )}
              </div>
            )}
            {!logsLoading && logs && logs.split('\n').map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '0 12px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ color: '#3d444d', minWidth: 38, textAlign: 'right',
                  userSelect: 'none', flexShrink: 0, fontSize: 11 }}>{i + 1}</span>
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  color: /error|Error|ERROR/.test(line) ? 'var(--color-danger-500)'
                       : /warn|Warn|WARN/.test(line)  ? 'var(--color-warning-500)' : 'var(--border)' }}>
                  {line}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TERMINAL TAB ── */}
      {tab === 'terminal' && (
        <div style={{ background: 'var(--ink)', borderRadius: 10, overflow: 'hidden', height: 'calc(100vh - 280px)',
          display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-2)',
            fontSize: 12, color: 'var(--muted-2)', fontFamily: 'monospace' }}>
            Connected to <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>{workload.name}</span>
            {' '}· namespace: <span style={{ color: 'var(--brand)' }}>{workload.namespace}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, fontFamily: 'monospace', fontSize: 12.5 }}>
            {termHistory.length === 0 && (
              <div style={{ color: 'var(--ink-2)' }}>Type a command below. Press Enter to run.</div>
            )}
            {termHistory.map((h, i) => (
              <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 2,
                color: h.type === 'input' ? 'var(--color-success-500)' : h.type === 'error' ? 'var(--color-danger-500)' : 'var(--border)' }}>
                {h.text}
              </div>
            ))}
            {termRunning && <div style={{ color: 'var(--ink-2)' }}>Running...</div>}
            <div ref={termBottomRef} />
          </div>
          <div style={{ background: 'var(--ink-2)', padding: '8px 14px', display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--color-success-500)', fontFamily: 'monospace', fontSize: 14, lineHeight: '32px' }}>$</span>
            <input
              autoFocus
              value={termInput}
              onChange={e => setTermInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !termRunning && handleTermExec()}
              placeholder="ls / or env or ps aux"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--border)', fontFamily: 'monospace', fontSize: 13 }}
            />
            <button onClick={handleTermExec} disabled={termRunning}
              style={{ padding: '4px 14px', background: 'var(--color-success-500)', color: '#fff',
                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
              Run
            </button>
            <button onClick={() => setTermHistory([])}
              style={{ padding: '4px 10px', background: 'var(--ink-2)', color: 'var(--border)',
                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── YAML TAB ── */}
      {tab === 'yaml' && (
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16 }}>
          {/* History list */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--surface-2)' }}>
              History
            </div>
            {manifestsLoading && (
              <div style={{ padding: 14, fontSize: 12, color: 'var(--muted-2)' }}>Loading…</div>
            )}
            {!manifestsLoading && manifests.length === 0 && (
              <div style={{ padding: 14, fontSize: 12, color: 'var(--muted-2)' }}>No manifest history yet.</div>
            )}
            {manifests.map(m => (
              <div key={m.id} onClick={() => selectManifest(m)}
                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12,
                  borderBottom: '1px solid var(--surface-2)',
                  background: selectedManifestId === m.id ? 'var(--brand-soft)' : 'transparent' }}>
                <div style={{ fontWeight: 600, color: m.success ? 'var(--ink)' : 'var(--color-danger-500)' }}>
                  {m.action}{!m.success && ' (failed)'}
                </div>
                <div style={{ color: 'var(--muted-2)', fontSize: 11 }}>
                  {m.applied_at ? new Date(m.applied_at).toLocaleString() : '—'}
                </div>
              </div>
            ))}
          </div>

          {/* Editor */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {selectedManifestId ? 'Viewing / editing selected version' : 'YAML'}
              </div>
              {canEdit && (
                <button onClick={handleApplyYaml} disabled={applyingYaml || !yamlDraft.trim()}
                  className="btn btn-primary btn-sm">
                  {applyingYaml ? 'Applying…' : yamlDirty ? '⟳ Re-apply edited YAML' : '⟳ Re-apply'}
                </button>
              )}
            </div>
            <textarea
              value={yamlDraft}
              onChange={e => { setYamlDraft(e.target.value); setYamlDirty(true); }}
              readOnly={!canEdit}
              rows={26}
              placeholder="Select a version on the left, or this workload has no recorded manifest yet."
              className="textarea"
              style={{ fontFamily: 'monospace', fontSize: 12, width: '100%' }}
            />
            {yamlMsg && (
              <div style={{ marginTop: 8, fontSize: 12,
                color: yamlMsg.toLowerCase().includes('fail') ? 'var(--color-danger-500)' : 'var(--color-success-700)' }}>
                {yamlMsg}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
